import React, { useState, useEffect, useMemo } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import Swal from 'sweetalert2'
import CustomPagination from '../../components/company_panel/common/custompagination'
import Sorting from '../../components/company_panel/common/sorting';
import Loader from '../../components/company_panel/common/loader'
import { capitalizeAllFirstLetters, capitalizeFirstLetter, checkPermission, formateDate, formateDateWithTime, getBrandId, getOutletBusinessDate, getOutletId, removeHyphens, TrimText } from '../../helpers/commonFunctions';
import notFound from '../../assets/company/img/no-record-found.png'
import { usePersistedListState } from '../../hooks/usePersistedListState';
import { AllWasteReason, DeleteWaste, ListWaste } from '../../services/company_panel/wasteManagement.services';
import { GetAllInventories } from '../../services/company_panel/inventoryService';
import { getAllMenuItems } from '../../services/company_panel/mealService';
import { LOADER_TIME } from '../../utils/Constants';
import CustomRangepicker from '../../components/company_panel/common/rangepicker'

// Default the date filter to the outlet's current business date (full-day range).
const getDefaultDateSearch = () => {
    const bd = getOutletBusinessDate();
    if (!bd) return {};
    const start = new Date(bd); start.setHours(0, 0, 0, 0);
    const end = new Date(bd); end.setHours(23, 59, 59, 999);
    return { business_date: { start, end } };
};

const WasteManagementList = () => {
    const navigate = useNavigate()
    const params = useParams()
    const location = useLocation()
    // persisted filters (page lives in the route) — restored on return from add/edit/view
    const { saved, save, clear } = usePersistedListState('waste_management');
    const [list, setList] = useState([])
    const [statsUpdate, setStatusUpdate] = useState("false")
    const [dataLength, setDataLength] = useState()
    const [itemPerPage] = useState(10)
    const [sorting, setSorting] = useState(saved?.sorting || {});
    const [defaultSorting, setDefaultSorting] = useState(true);
    const [page, setPage] = useState(Number(params?.id) || 1);
    const [loader, setLoader] = useState(true)
    const [search, setSearch] = useState(saved?.search || getDefaultDateSearch)
    const businessDate = getOutletBusinessDate();
    const defaultDateFilter = useMemo(() => (businessDate ? [businessDate, businessDate] : []), [businessDate]);
    const [globalSearch, setGlobalSearch] = useState(saved?.globalSearch || "");
    const [isBrandChanged, setIsBrandChanged] = useState(false);
    const [isOutletChanged, setIsOutletChanged] = useState(false);
    const [resetdate, setResetDate] = useState(false);
    const [showItemsModal, setShowItemsModal] = useState(false);
    const [viewItems, setViewItems] = useState([]);
    const [viewBusinessDate, setViewBusinessDate] = useState(null);
    const [inventoryMap, setInventoryMap] = useState({});
    const [menuItemMap, setMenuItemMap] = useState({});
    const [reasonMap, setReasonMap] = useState({});

    const getWastedItemName = (item) => {
        const name = item?.item_type === 'raw-material' ? inventoryMap[item?.inventory_id] : menuItemMap[item?.menu_item_id];
        return name ? capitalizeAllFirstLetters(name) : 'N/A';
    };

    const getReasonName = (item) => {
        const reason = reasonMap[item?.reason_for_waste];
        return reason ? capitalizeAllFirstLetters(reason) : 'N/A';
    };


    useEffect(() => {
        if (search) {
            setLoader(true);
            setTimeout(() => {
                const formData = new FormData()
                formData.append("page", page)
                formData.append("per_page", itemPerPage)
                formData.append("sort", JSON.stringify(sorting))
                formData.append("search", JSON.stringify(search))
                formData.append("global_search", globalSearch)
                formData.append('brand_id', getBrandId());
                formData.append('outlet_id', getOutletId());

                ListWaste(formData).then(data => {
                    setDataLength(data.data.response.count);
                    setList(data && data.data && data.data.response ? data.data.response.rows : [])
                    setLoader(false);
                }).catch(error => {
                    console.log("error ====> ", error);
                    setLoader(false);
                })
            }, LOADER_TIME);
        }
    }, [location, statsUpdate, sorting, search, globalSearch, isBrandChanged, isOutletChanged]);


    useMemo(() => {
        document.addEventListener('brand-changed', function () {
            setIsBrandChanged(!isBrandChanged);
        });
        document.addEventListener('outlet-changed', function () {
            setIsOutletChanged(!isOutletChanged);
        });
    });

    // Item name & reason name aren't in the list response, so resolve ids -> names client-side.
    useEffect(() => {
        const inventoryForm = new FormData();
        inventoryForm.append('brand_id', getBrandId());
        GetAllInventories(inventoryForm).then(data => {
            const rows = data?.data?.response?.rows || [];
            const map = {};
            rows.forEach(row => { map[row.id] = row.item_name; });
            setInventoryMap(map);
        }).catch(error => { console.log("error ====> ", error); });

        getAllMenuItems({ brand_id: getBrandId() }).then(data => {
            const rows = data?.data?.response?.rows || [];
            const map = {};
            rows.forEach(row => { map[row.id] = row.item_name; });
            setMenuItemMap(map);
        }).catch(error => { console.log("error ====> ", error); });

        AllWasteReason({ brand_id: getBrandId() }).then(data => {
            const rows = data?.data?.response || [];
            const map = {};
            rows.forEach(row => { map[row.id] = row.reason; });
            setReasonMap(map);
        }).catch(error => { console.log("error ====> ", error); });
    }, [isBrandChanged]);

    // persist filters on every change (page lives in the route, not the store)
    useEffect(() => {
        save({ sorting, search, globalSearch });
    }, [sorting, search, globalSearch])

    const resetFilter = (e) => {
        e.preventDefault()
        setGlobalSearch("")
        setSearch(getDefaultDateSearch())
        setResetDate(!resetdate)
        clear()
    }

    function deleteFunction(row) {
        Swal.fire({
            customClass: 'swal-wide',
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#2f9bff',
            cancelButtonColor: '#dc3545',
            confirmButtonText: 'Yes, delete it!'
        }).then((result) => {
            if (result.isConfirmed) {
                const formdata = new FormData()
                formdata.append("id", row)
                DeleteWaste(formdata).then(data => {
                    if (data.status === 200) {
                        Swal.fire({
                            customClass: 'swal-wide',
                            position: 'absolute',
                            icon: 'success',
                            title: 'Waste deleted successfully',
                            showConfirmButton: false,
                            timer: 2000,
                            toast: true,
                            position: 'top-right',
                        })
                        setStatusUpdate(!statsUpdate)
                    }
                }).catch(error => {
                    console.log("deleteError",error);
                })
            }
        })
    }

    const viewFunction = (row) => {
        navigate(`/company/store_manager/waste_management/${page}/view/${row}`)
    }
    const editFunction = (row) => {
        navigate(`/company/store_manager/waste_management/${params?.id}/edit/${row.id}`)
    }
    const handleSort = (e, column) => {
        setDefaultSorting(false);
        let sort = { order: 0, column: column };
        if (e.target.classList.contains("assc")) {
            sort.order = -1;
        } else {
            sort.order = 1;
        }
        setSorting(sort);
    }

    const prepareSearch = (key, value) => {
        let sr = { ...search };
        if (String(value).length > 0) {
            sr[key] = value;
        } else {
            delete sr[key];
        }
        setSearch(sr);
    }

    return (
        <>
            <div className="animation_fade">
                <div className='page-header d-flex align-items-center'>
                    <div className='page-header-left'>
                        <h4>Waste management</h4>
                    </div>
                    <div className='page-header-right ms-auto'>
                        <button className="btn btn-primary ms-2" onClick={() => navigate(`/company/store_manager/waste_management/reasons/list/1`)}>Reason</button>
                        {checkPermission('store_waste_management', 'add') ?
                        <button className="btn btn-primary ms-2" onClick={() => navigate(`/company/store_manager/waste_management/${page}/add`)}>Add Waste</button>
                        : ''}
                    </div>
                </div>
                <div className="card custom-card overflow-hidden">
                    <div className='gap-20'>
                        <div className='filter-header d-flex justify-content-between align-items-center'>
                            <div className='d-flex gap-3 align-items-center'>
                                <div className="form-group mb-0 rangepicker_container">
                                    <input type="search" className="form-control" value={globalSearch} placeholder="Search" onChange={(e) => { setGlobalSearch(e.target.value) }} />
                                </div>
                            </div>
                            <div className='d-flex gap-3 align-items-center'>
                                <div className="form-group mb-0 rangepicker_container">
                                    <CustomRangepicker GetDateRange={(e) => { prepareSearch("business_date", e) }} resetdate={resetdate} defaultDateFilter={defaultDateFilter} business_date={true} />
                                </div>
                                <div className='d-flex gap-3 align-items-center'>
                                    <button type="reset" value='Reset' onClick={resetFilter} className="btn btn-primary mr-2">Reset Filter</button>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="tabler-wrap">
                        <div className="table-responsive">
                            <table className="table table-custom">
                                <thead>
                                    <tr>
                                        <th width="5%">
                                            <div className='d-inline-flex align-items-start'>
                                                <span className='me-2'>Id</span>
                                                <Sorting sort={sorting} handleSort={handleSort} defaultSorting={defaultSorting} column="id" />
                                            </div>
                                        </th>
                                        <th width="12%">
                                            <div className='sorting_column'>
                                                <span>Business Date</span>
                                                <Sorting sort={sorting} handleSort={handleSort} column="business_date" />
                                            </div>
                                        </th>
                                        <th width="10%">
                                            <div className='sorting_column'>
                                                <span>Amount Loss</span>
                                            </div>
                                        </th>
                                        <th width="8%">
                                            <div className='sorting_column'>
                                                <span>Wasted Items Count</span>
                                            </div>
                                        </th>
                                        <th width="26%">
                                            <div className='sorting_column'>
                                                <span>Wasted Items</span>
                                            </div>
                                        </th>
                                        <th width="15%">
                                            <div className='sorting_column'>
                                                <span>Created At</span>
                                            </div>
                                        </th>
                                        <th width="4%"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loader ?
                                        <tr>
                                            <td colSpan={7}><Loader /></td>
                                        </tr> : <>
                                            {list.length ? list.map((row, i) => {
                                                const wastedItems = row?.wasted_items || [];
                                                const names = wastedItems.map(getWastedItemName);
                                                return (
                                                <tr key={i}>
                                                    <td>{row.id}</td>
                                                    <td>{row?.business_date ? formateDate(row?.business_date, false) : 'N/A'}</td>
                                                    <td>{row?.amount_loss != null && row?.amount_loss !== '' ? `${row.amount_loss} SAR` : 'N/A'}</td>
                                                    <td>{wastedItems.length}</td>
                                                    <td>
                                                        {names.length ? (
                                                            <>
                                                                {names.slice(0, 2).join(', ')}{names.length > 2 ? ' ...' : ''}
                                                                <span className='text-primary cp ms-2' style={{ cursor: 'pointer' }} onClick={() => { setViewItems(wastedItems); setViewBusinessDate(row?.business_date); setShowItemsModal(true); }}>Show all</span>
                                                            </>
                                                        ) : 'N/A'}
                                                    </td>
                                                    <td>{row?.created_at ? formateDateWithTime(row?.created_at, true) : 'N/A'}</td>
                                                    <td className='text-end'>
                                                        <div className="dropdown  d-inline-block">
                                                            <button className="btn btn-icon btn-secondary" type="button" data-bs-toggle="dropdown" aria-expanded="false">
                                                                <i className='ti ti-dots-vertical'></i>
                                                            </button>
                                                            <ul className="dropdown-menu">
                                                            {checkPermission('store_waste_management', 'view') ?
                                                                <li><div className='dropdown-item' onClick={() => { viewFunction(row.id) }}><i className="ti ti-eye" aria-hidden="true"></i> View details</div></li>
                                                                : '' }
                                                            </ul>
                                                        </div>
                                                    </td>
                                                </tr>
                                                );
                                            }) : <tr>
                                                <td colSpan={7} className="text-center">
                                                    <img src={notFound} style={{ width: "5%" }} alt="Not Found" />
                                                    <p>No Record.</p>
                                                </td>
                                            </tr>}
                                        </>}
                                </tbody>
                            </table>
                        </div>
                        <div className='tabler-footer'>
                            <div className='d-flex align-items-center'>
                                <div id="example1_info" role="status" className="table-result" aria-live="polite"><b>Total Records : {dataLength ? dataLength : "0"}</b></div>
                                {dataLength && dataLength > 0 ?
                                    <CustomPagination datalength={dataLength} itemperpage={itemPerPage} setPage={setPage} currentPage={page} pageRoute={[{ name: "waste_management", path: "/company/store_manager/waste_management/list" }]} /> : ""}
                            </div>
                        </div>
                    </div>
                    <div>
                    </div>
                </div>
            </div>

            {showItemsModal && (
                <>
                    <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1" onClick={() => setShowItemsModal(false)}>
                        <div className="modal-dialog modal-dialog-centered modal-lg" onClick={(e) => e.stopPropagation()}>
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title">Wasted Items</h5>
                                    <button type="button" className="btn-close" aria-label="Close" onClick={() => setShowItemsModal(false)}></button>
                                </div>
                                <div className="modal-body">
                                    <p className='mb-3'><b>Business Date:</b> {viewBusinessDate ? formateDate(viewBusinessDate, false) : 'N/A'}</p>
                                    <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                                    <table className="table table-custom">
                                        <thead>
                                            <tr>
                                                <th>Item Type</th>
                                                <th>Item Name</th>
                                                <th>Quantity</th>
                                                <th>Amount Loss</th>
                                                <th>Reason</th>
                                                <th>Order Id</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {viewItems.map((it, idx) => (
                                                <tr key={idx}>
                                                    <td>{it?.item_type ? capitalizeAllFirstLetters(removeHyphens(it?.item_type)) : 'N/A'}</td>
                                                    <td>{getWastedItemName(it)}</td>
                                                    <td>{it?.quantity ?? 'N/A'}</td>
                                                    <td>{it?.amount_loss != null && it?.amount_loss !== '' ? `${it.amount_loss} SAR` : 'N/A'}</td>
                                                    <td>{getReasonName(it)}</td>
                                                    <td>{it?.order_id ? `#order ${it?.order_id}` : 'N/A'}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="modal-backdrop fade show" onClick={() => setShowItemsModal(false)}></div>
                </>
            )}
        </>
    );
}

export default WasteManagementList